<template>
  <aside v-if="modelValue" class="modal" :class="classes">
    <div class="wrap" @mousedown.self="onClickBackdrop">
      <div class="content-wrapper">
        <div class="content">
          <div v-if="showHeader" class="header">
            <img :src="LOGO_PATH_FULL" class="logo" :alt="LOGO_TITLE" :title="LOGO_TITLE" />
          </div>
          <slot />
          <Icon
            v-if="showClose"
            class="close"
            icon="close"
            size="m"
            :qa-locator-id="QA_ID_CLOSE_BUTTON"
            @click.native="onClose"
          />
        </div>
      </div>
    </div>
  </aside>
</template>

<script setup lang="ts">
import { usePageScrollLock } from '~/composables/use-page-scroll-lock';
import { computed, nextTick, onBeforeUnmount, ref, watch } from 'vue';
import type { ModalType, ModalPosition } from './types';

import Icon from '~/components/components/icon/Icon.vue';

import { LOGO_PATH_FULL, LOGO_TITLE } from '~/const/Logo/MapsConst';

// -- Используется в тестировании, изменения необходимо согласовать с QA
const QA_ID_CLOSE_BUTTON: string = 'modal-close-button';
// --

const context = useNuxtApp();
const modelValue = defineModel();

const emit = defineEmits(['close']);

const props = defineProps({
  type: {
    type: String as PropType<ModalType>,
    default: 'modal',
  },
  position: {
    type: String as PropType<ModalPosition>,
    default: 'left',
  },
  showClose: {
    type: Boolean,
    default: true,
  },
  showHeader: {
    type: Boolean,
    default: false,
  },
  backdropClickClose: {
    type: Boolean,
    default: true,
  },
  lockScroll: {
    type: Boolean,
    default: true,
  },
});

const isShow: Ref<boolean> = ref(false);
let showRequest = 0;
let firstFrame: number | null = null;
let secondFrame: number | null = null;

const isReadyToShow = computed((): boolean => {
  return !!modelValue.value;
});

usePageScrollLock(() => isReadyToShow.value && props.lockScroll);

const classes = computed((): object => {
  return {
    show: isShow.value,
    [`type-${props.type}`]: true,
    [`position-${props.position}`]: true,
  };
});

function onClickBackdrop() {
  if (!props.backdropClickClose) {
    return;
  }
  onClose();
}

function onClose() {
  emit('close');
  context.$emitter.emit('hide-modal');
  // TODO: убрать когда все модальные окна будут перенесены в ModalManager
  modelValue.value = false;
}

watch(isShow, () => {
  if (props.type === 'fullscreen') {
    context.$emitter.emit('on-toggle-fullscreen-modal', props.modelValue);
  }
  // задача 5397, некорректная верстка
  if (props.type === 'sidebar') {
    context.$emitter.emit('on-toggle-modal', props.modelValue);
  }
});

function cancelShowFrames(): void {
  if (firstFrame !== null) cancelAnimationFrame(firstFrame);
  if (secondFrame !== null) cancelAnimationFrame(secondFrame);
  firstFrame = null;
  secondFrame = null;
}

watch(
  isReadyToShow,
  async (isReady) => {
    const request = ++showRequest;
    cancelShowFrames();
    isShow.value = false;

    if (!isReady) return;

    await nextTick();
    if (request !== showRequest) return;

    // Первый кадр фиксирует начальное состояние, второй запускает CSS-переход.
    firstFrame = requestAnimationFrame(() => {
      firstFrame = null;
      secondFrame = requestAnimationFrame(() => {
        secondFrame = null;
        if (request === showRequest) isShow.value = true;
      });
    });
  },
  { immediate: true }
);

onBeforeUnmount(() => {
  showRequest++;
  cancelShowFrames();
});
</script>

<style lang="scss" scoped>
.modal {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  max-height: 100vh;
  z-index: 101;
  overflow-x: hidden;
  overflow-y: auto;
  overscroll-behavior: contain;
  visibility: hidden;
  transition: all 0.1s linear;
  opacity: 0;
  background-color: rgba(0, 0, 0, 0.5);

  &.type {
    &-curtain {
      overflow-y: hidden;

      > .wrap {
        margin: 0;
        min-height: 100%;
        align-items: flex-end;
        transform: translateY(25px);
        opacity: 0;
      }

      > .wrap > .content-wrapper {
        position: relative;
        max-height: 100vh;
        max-height: 100dvh;
        overflow-y: auto;
        overscroll-behavior: contain;
        -webkit-overflow-scrolling: touch;

        @include tablet {
          width: 100%;
          max-width: 520px;
        }
      }

      > .wrap > .content-wrapper > .content {
        padding-top: 36px;
        border-radius: $border-radius-m 20px 0 0;
      }
    }

    &-fullscreen {
      > .wrap {
        margin: 0;
      }

      > .wrap > .content-wrapper > .content {
        padding: 0;
        border-radius: 0;
        height: 100vh;
        width: 100vw;
      }

      > .wrap > .content-wrapper > .content > .close {
        width: 56px;
        height: 56px;
        top: 24px;
        right: 0;
      }

      > .wrap > .content {
        &:has(.planner-survey) {
          > .close {
            top: 32px;
            right: 16px;
            background-color: #fff;
            transition:
              background-color $transition,
              color $transition;

            &:hover {
              background-color: $color-brand;
              color: #fff;
            }
          }
        }
      }
    }

    &-sidebar {
      > .wrap {
        height: 100%;
        margin: 0;
        max-width: 100vw;
        max-height: 100vh;
        position: fixed;
        top: 0;
        width: 100vw;
        overflow: hidden;
        display: flex;
      }

      > .wrap > .content-wrapper {
        height: 100%;

        @include mobile_sm {
          width: 100vw;
        }
      }

      > .wrap > .content-wrapper > .content {
        overflow: auto;
        overscroll-behavior: contain;
        height: 100%;
        position: static;
        top: 0;
        left: 0;
        min-height: 100vh;
        max-width: calc(100vw - 120px);
        margin: 0;
        background-color: #fff;
        padding: 16px;
        border-radius: $border-radius-xl 0 0 $border-radius-xl;

        @include mobile {
          max-width: calc(100vw - 16px);
        }

        @include mobile_sm {
          max-width: 100vw;
          border-radius: 0 !important;
        }
      }

      > .wrap > .content-wrapper > .content > .close {
        width: 56px;
        height: 56px;
        top: 16px;
        right: -20px;
        left: unset;

        @include mobile_sm {
          right: -8px;
        }
      }

      &.position {
        &-right {
          > .wrap {
            transform: translateX(25px);
            right: 0;
            width: 100vw;
            left: unset;
            justify-content: flex-end;
          }
          > .wrap > .content-wrapper > .content {
            border-radius: $border-radius-xl 0 0 $border-radius-xl;
          }
        }

        &-left {
          > .wrap {
            transform: translateX(-25px);
            max-width: 100vw;
            left: 0;
            justify-content: flex-start;
          }
          > .wrap > .content-wrapper > .content {
            border-radius: 0 $border-radius-l $border-radius-l 0;
          }
        }
      }
    }

    &-modal {
      > .wrap > .content-wrapper > .content {
        border-radius: $border-radius-l;

        @include mobile {
          max-width: calc(100vw - 32px);
          width: 100vw;
        }
      }

      &.position {
        &-top {
          > .wrap > .content-wrapper {
            position: absolute;
            top: 0;
          }
        }
      }
    }
  }

  &.show {
    visibility: visible;
    opacity: 1;

    > .wrap {
      transform: translateY(0);
      opacity: 1;
    }
  }

  > .wrap {
    margin: 32px 16px;
    min-height: calc(100% - 64px);
    display: flex;
    align-items: center;
    justify-content: center;
    transform: translateY(-25px);
    transition:
      transform $transition ease-in-out,
      opacity $transition ease-in-out;

    @include mobile {
      margin: 16px;
    }
  }

  > .wrap > .content-wrapper {
    position: relative;
  }

  > .wrap > .content-wrapper > .content {
    width: max-content;
    display: flex;
    flex-direction: column;
    position: relative;
    background: $color-white;
    border-radius: 24px;
    box-shadow: 0 8px 32px rgba(0, 0, 0, 0.15);

    @include mobile {
      width: 100%;
    }
  }

  > .wrap > .content-wrapper > .content > .header {
    height: 92px;
    padding: 24px 40px;
    box-shadow: $box-shadow-default;
    display: flex;
  }

  > .wrap > .content-wrapper > .content > .close {
    cursor: pointer;
    position: absolute;
    top: 18px;
    right: 18px;
    color: $color-potato-50;

    @include mobile {
      top: 20px;
      right: 14px;
    }

    @media (hover: hover) {
      &:hover {
        color: #000;
      }
    }
  }

  > .wrap > .content-wrapper > .content:has(.hide-close-button) .close {
    display: none;
  }

  > .wrap > .content-wrapper > .content:has(> .header) .close {
    top: 34px;
    right: 34px;
  }
}
</style>
